/* eslint-disable react/no-access-state-in-setstate */
import Axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import MultiSelectDropdown from '../../components/Elements/MultiSelectDropdown/MultiSelectDropdown';
import EmployerDatabaseResultTable from '../../components/Employer/EmployerDatabaseResultTable/EmployerDatabaseResultTable';
import FlexibilityTipModal from '../../components/Employer/FlexibilityTipModal/FlexibilityTipModal';
import BasicDropdown from '../../components/Input/BasicDropdown/BasicDropdown';
import Button from '../../components/Input/Button/Button';
import FormElementWithLabel from '../../components/Input/FormElementWithLabel/FormElementWithLabel';
import InputField from '../../components/Input/InputField/InputField';
/* COMPONENTS */
import SinglePageLayout from '../../components/Layout/SinglePageLayout/SinglePageLayout';
import PageNav from '../../components/Management/Layout/PageNav/PageNav';
import HeaderSection from '../../components/Resources/HeaderSection/HeaderSection';
import SearchContainer from '../../components/Resources/SearchContainer/SearchContainer';
import { SnowplowPageViewTracker } from '../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import { throttle } from '../../utilities/helperFunctions';
/* CONSTANTS */
import {
    companyFieldLabelProps,
    industryFieldLabelProps,
} from '../MaternityLeaveResourceCenter/constants';
import {
    buildJobTypesQuery,
    convertToArray,
    jobTypesFieldLabelProps,
    mapJobTypes,
    PAGE_LIMIT,
    removeLabel,
    SEARCH_API,
    TABLE_HEADERS,
    THROTTLE_INTERVAL,
} from './constants';
import { PAGE_PROPS } from '../defaultProps';

/* STYLESHEET */
import './WorkLifeBalanceGuide.scss';

export default class WorkLifeBalanceGuide extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            showTipModal: false,
            companyName: '',
            industry: '',
            jobTypes: [],
            sortBy: 'companyName',
            order: 'asc',
            limit: 20,
            page: 1,
            jobTypeError: '',
            searchResults: [],
            tip: {
                companyName: null,
                companyId: null,
            },
        };

        this.throttlePushUrl = throttle(
            this.pushUrlToWindow,
            THROTTLE_INTERVAL
        );
    }

    componentDidMount = () => {
        const queryValues = this.getUrlParams();
        this.setState(queryValues, this.search);
    };

    getUrlParams = () => {
        const urlParams = new URLSearchParams(window.location.search);

        const companyName = urlParams.get('company') || '';
        const industry = urlParams.get('industry') || '';
        const page = parseInt(urlParams.get('page'), 10) || 1;
        const sortBy = urlParams.get('sortBy') || this.state.sortBy;
        const order = urlParams.get('order') || this.state.order;
        const jobTypes = convertToArray(urlParams.get('jobtype'));

        return { companyName, industry, jobTypes, page, sortBy, order };
    };

    setUrlParams = () => {
        const {
            companyName,
            industry,
            jobTypes,
            page,
            sortBy,
            order,
        } = this.state;

        if (window.history.pushState) {
            const { protocol, host, pathname } = window.location;
            const companyQuery = companyName ? `company=${companyName}&` : '';
            const industryQuery = industry ? `industry=${industry}&` : '';
            const jobTypesQuery = jobTypes.length
                ? `jobtype=${buildJobTypesQuery(jobTypes)}&`
                : '';
            const sortByQuery = sortBy ? `sortBy=${sortBy}&` : '';
            const orderQuery = order ? `order=${order}&` : '';
            const pageQuery = `page=${page}`;

            const url = `${protocol}//${host}${pathname}`;
            const queries = `${companyQuery}${industryQuery}${jobTypesQuery}${sortByQuery}${orderQuery}${pageQuery}`;

            const newUrl = `${url}?${queries}`;
            this.throttlePushUrl(newUrl);
        }
    };

    pushUrlToWindow = (newUrl) => {
        window.history.replaceState({}, '', newUrl);
    };

    handleModalClose = () => this.setState({ showTipModal: false });

    handlePrevious = () =>
        this.setState({ page: this.state.page - 1 }, this.search);

    handleNext = () =>
        this.setState({ page: this.state.page + 1 }, this.search);

    handleSelect = (industry) =>
        this.setState({ industry, page: 1 }, this.search);

    handleCompanyChange = (companyName) =>
        this.setState({ companyName, page: 1 }, this.search);

    handleJobTypeChange = (jobTypes) =>
        this.setState({ jobTypes, page: 1 }, this.search);

    handleJobTypeError = (error) => this.setState({ jobTypeError: error });

    handleReset = () => {
        this.setState(
            {
                companyName: '',
                industry: '',
                jobTypes: [],
                page: 1,
            },
            this.search
        );
    };

    handleTipClick = (companyId, companyName) => {
        this.setState({
            showTipModal: true,
            tip: {
                companyId,
                companyName,
            },
        });
    };

    handleSort = (sortBy, order) =>
        this.setState({ sortBy, order }, this.search);

    flattenDataForTable = (data) => {
        const { comments, companyId, companyName, industry, jobTypes } = data;

        return [
            companyId,
            companyName,
            industry,
            jobTypes.map(mapJobTypes),
            comments,
        ];
    };

    getCancelToken = () => {
        if (typeof this._source !== typeof undefined) {
            this._source.cancel('Operation canceled due to new request.');
        }

        // save the new request for cancellation
        this._source = Axios.CancelToken.source();

        return {
            cancelToken: this._source.token,
        };
    };

    search = async () => {
        const {
            companyName,
            industry,
            jobTypes,
            sortBy,
            order,
            limit,
            page,
        } = this.state;

        this.setUrlParams();
        const cancelToken = this.getCancelToken();

        const payload = {
            filters: {
                companyName,
                industry,
                jobTypes: jobTypes.map(removeLabel),
            },
            sorting: { type: sortBy, order },
            companyPagination: { limit, page },
        };

        try {
            const { data } =
                (await Axios.post(SEARCH_API, payload, cancelToken)) || {};
            this.setState({
                searchResults: data.map(this.flattenDataForTable),
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log('e: ', e);
        }
    };

    renderSearchFields = () => {
        return (
            <SearchContainer onReset={this.handleReset}>
                <FormElementWithLabel fieldLabelProps={companyFieldLabelProps}>
                    <InputField
                        name="company-name"
                        placeholder="Enter a company"
                        inputValue={this.state.companyName}
                        onChange={this.handleCompanyChange}
                    />
                </FormElementWithLabel>

                <FormElementWithLabel fieldLabelProps={industryFieldLabelProps}>
                    <BasicDropdown
                        defaultLabel="Select industry"
                        options={this.props.searchBar.industryList}
                        onChange={this.handleSelect}
                        selectedValue={this.state.industry}
                        shouldDisableDefault
                    />
                </FormElementWithLabel>

                <FormElementWithLabel
                    fieldLabelProps={jobTypesFieldLabelProps}
                    error={this.state.jobTypeError}
                >
                    <MultiSelectDropdown
                        dropdownOptions={this.props.searchBar.jobTypes}
                        selectedOptions={this.state.jobTypes}
                        onChange={this.handleJobTypeChange}
                        onError={this.handleJobTypeError}
                    />
                </FormElementWithLabel>

                <Button
                    formAction="reset"
                    className="reset-button"
                    type="secondary-blue-gray"
                    value="Clear"
                />
            </SearchContainer>
        );
    };

    renderSearchResultsTable = () => {
        return (
            <div className="data-table">
                <EmployerDatabaseResultTable
                    tableHeaders={TABLE_HEADERS}
                    rowData={this.state.searchResults.slice(0, 20)}
                    onTipClick={this.handleTipClick}
                    onSort={this.handleSort}
                    sortType={this.state.sortBy}
                    sortOrder={this.state.order}
                />
            </div>
        );
    };

    renderPageNavigation = () => {
        const goPrev = this.state.page !== 1 ? this.handlePrevious : null;
        const goNext =
            this.state.searchResults.length > PAGE_LIMIT
                ? this.handleNext
                : null;
        const hasSearchResults = this.state.searchResults.length > 0;

        if (hasSearchResults) {
            return <PageNav goToPrevPage={goPrev} goToNextPage={goNext} />;
        }
    };

    render() {
        return (
            <div className="page-WorkLifeBalanceGuide">
                <SnowplowPageViewTracker />
                <SinglePageLayout PAGE_PROPS={this.props.PAGE_PROPS}>
                    <SinglePageLayout.Header>
                        <HeaderSection
                            title="Work-Life Balance Guide"
                            subheading="How flexible is your employer?"
                            description="Our Work-Life Balance Guide helps you discover which employers offer part-time jobs, flexible working policies and telecommuting positions. You can search by industry and company to see what other women have said about job flexibility at their firms and compare employers."
                        />
                    </SinglePageLayout.Header>

                    <SinglePageLayout.Content>
                        <main>
                            {this.renderSearchFields()}
                            {this.renderSearchResultsTable()}
                            {this.renderPageNavigation()}
                        </main>

                        {this.state.showTipModal && (
                            <FlexibilityTipModal
                                companyId={this.state.tip.companyId}
                                companyName={this.state.tip.companyName}
                                jobTypes={this.props.searchBar.jobTypes}
                                showModal={this.state.showTipModal}
                                onClose={this.handleModalClose}
                            />
                        )}
                    </SinglePageLayout.Content>
                </SinglePageLayout>
            </div>
        );
    }
}

WorkLifeBalanceGuide.propTypes = {
    PAGE_PROPS: PAGE_PROPS.isRequired,
    /**
     * object searchbar of jobTypes and industryList
     */
    searchBar: PropTypes.shape({
        jobTypes: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                type: PropTypes.string,
            })
        ).isRequired,
        industryList: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.string,
            })
        ).isRequired,
    }).isRequired,
};
